<template>
  <v-card color="#05182d" class="pa-4 custom-chart-card">
    <!-- Header -->
    <v-list-item class="chart-header">
      <v-list-item-title class="chart-title">{{ title }}</v-list-item-title>
      <v-spacer></v-spacer>
      <v-list-item-action>
        <date-picker v-model="tanggal" @input="onDateInput"></date-picker>
      </v-list-item-action>
    </v-list-item>

    <v-divider class="cyan-divider"></v-divider>

    <!-- Tabs -->
    <v-tabs v-model="tab" background-color="transparent" dark>
      <v-tab v-for="item in charts" :key="item.title">{{ item.title }}</v-tab>
    </v-tabs>

    <!-- Chart -->
    <v-tabs-items
      v-model="tab"
      background-color="transparent"
      class="chart-tabs-items"
    >
      <v-tab-item
        v-for="(item, idx) in charts"
        :key="item.title"
        class="chart-tab-item"
      >
        <scatter
          :chart-data="chartItem(idx + 1)"
          :options="options"
          :height="height"
        />
      </v-tab-item>
    </v-tabs-items>

    <!-- Footer -->
    <v-list-item-title class="text-caption text-cyan px-10 mt-2">
      {{ footer }}&nbsp;
    </v-list-item-title>
  </v-card>
</template>

<script>
import Scatter from "@/components/Scatter.js";
import DatePicker from "@/components/Editor/DatePicker.vue";

export default {
  components: { Scatter, DatePicker },
  data() {
    return {
      tab: 0,
      tanggal: "",
      colors: [
        "#00ffff",
        "#3a86ff",
        "#ff006e",
        "#2a9d8f",
        "#e9c46a",
        "#8338ec",
      ],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          labels: {
            fontColor: "#00ffff",
            fontSize: 12,
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                color: "rgba(0, 255, 255, 0.2)",
                zeroLineColor: "#00ffff",
              },
              ticks: {
                fontColor: "#00ffff",
              },
              type: "time",
              time: {
                unit: "minute",
                displayFormats: { minute: "HH:mm" },
                tooltipFormat: "HH:mm",
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                color: "rgba(0, 255, 255, 0.2)",
                zeroLineColor: "#00ffff",
              },
              ticks: {
                fontColor: "#00ffff",
              },
            },
          ],
        },
        tooltips: {
          mode: "index",
          intersect: false,
          titleFontColor: "#00ffff",
          bodyFontColor: "#00ffff",
          backgroundColor: "rgba(15, 35, 70, 0.95)",
          borderColor: "#00ffff",
          borderWidth: 1,
        },
      },
    };
  },
  props: {
    date: { type: String, default: "" },
    title: { type: String, default: "Title" },
    datasets: { type: Array, default: () => [{ x: 0, y: 1 }] },
    headers: { type: Array, default: () => [] },
    labels: { type: Array, default: () => [] },
    footer: { type: String, default: "" },
    height: { type: Number, default: 250 },
    charts: { type: Array, default: () => [{ title: "Chart" }] },
  },
  mounted() {
    this.tanggal = this.date;
  },
  computed: {
    chdata() {
      const result = [];
      if (this.headers.length > 1) {
        const xaxis = this.headers[0];
        const xfield = xaxis.field;
        const istime = xaxis.unit === "time";

        for (let i = 1; i < this.headers.length; i++) {
          const yfield = this.headers[i].field;
          const rows = this.datasets.map((dsrow) => {
            let xvalue = dsrow[xfield];
            if (istime) xvalue = new Date(xvalue).getTime();
            return { x: xvalue, y: dsrow[yfield] };
          });
          result.push(rows);
        }
      }
      return result;
    },
  },
  methods: {
    chartItem(index) {
      const datasets = [];
      for (let i = 1; i < this.headers.length; i++) {
        if (i === 0 || i === index) {
          const item = this.headers[i];
          const color = this.colors[i % this.colors.length];
          datasets.push({
            label: item.caption,
            borderColor: color,
            backgroundColor: this.gradientFill(color),
            borderWidth: 2,
            pointRadius: 0,
            showLine: true,
            fill: true,
            data: this.chdata[i - 1],
          });
        }
      }
      return { datasets };
    },
    gradientFill(color) {
      // gradasi lembut dari warna utama ke transparan
      return color + "33"; // tambahkan transparansi hex
    },
    onDateInput(value) {
      this.$emit("datechanged", value);
    },
  },
};
</script>

<style scoped>
.chart-tabs-items {
  background: transparent !important;
}

.chart-tab-item {
  background: radial-gradient(
    circle at top,
    rgba(0, 255, 255, 0.08),
    transparent 80%
  );
  border-radius: 10px;
  padding: 8px;
}

.v-card {
  background: rgba(15, 25, 35, 0.65) !important;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 234, 255, 0.2) !important;
  border-radius: 16px !important;
  box-shadow: 0 4px 20px rgba(0, 234, 255, 0.08);
  color: #e0f7fa !important;
}

.custom-chart-card {
  backdrop-filter: blur(18px);
  background: rgba(15, 35, 70, 0.6);
  border: 1px solid rgba(0, 255, 255, 0.3);
  box-shadow: 0 0 25px rgba(0, 200, 255, 0.15);
  border-radius: 12px;
  transition: all 0.3s ease;
  padding: 0px !important;
  height: 100%;
}

.chart-header {
  background-color: rgba(15, 35, 70, 0.7);
  color: #00ffff;
}

.chart-title {
  font-weight: 700;
  color: #00ffff;
}

.cyan-divider {
  border-color: #00ffff !important;
}

.text-cyan {
  color: #00ffff !important;
}

.v-tab {
  color: #b8eaff !important;
  transition: 0.3s;
}

.v-tab:hover {
  color: #00eaff !important;
  text-shadow: 0 0 8px rgba(0, 234, 255, 0.4);
}

.v-tab.v-tab--active {
  color: #00eaff !important;
  border-bottom: 2px solid #00eaff !important;
}
</style>
